import { Box, Grid, Paper, Typography } from '@mui/material';
import { useSignInProcess } from 'hooks/useSignInProcess';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import { useLoginFlow } from 'hooks/useLoginFlow';
import { resetAllValidationInfo, resetValidationInfo, setValidationInfo } from 'redux/actions/internal/general/validation';
import { FormEvent, useState } from 'react';
import { AlertDialog } from 'components/organisms/Dialogs';
import RegisterInfo from '../components/RegisterInfo';
import { useAuth } from 'context/authentication';
import useTranslation from 'next-translate/useTranslation';
import { secondaryAuth } from 'config/firebase';
import { resetUsersLoginInfo, updateUsersLoginInfo } from 'redux/actions/internal/general/users';
import { useLoginContext, useLoginUserProviders } from 'context/login-context';
import { useExecutor } from 'hooks/useExecutor';
import { useGenericAlert } from 'hooks/useGenericAlert';

interface IRegisterInfoPaperProps {
    onLogin?: (user: any) => void;
}

const RegisterInfoPaper = ({ onLogin }: IRegisterInfoPaperProps) => {
    const dispatch = useDispatch();

    const signInProcess = useSignInProcess();
    const authentication = useLoginFlow();
    const { verifyPhoneNumber, getSignInMethodsForEmail, checkPhone, signInWithEmailAndPassword, postUserToken } = useAuth();

    const [loading, setLoading] = useState<boolean>(false);
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [existingPhonePopupOpen, setExistingPhonePopupOpen] = useState<boolean>(false);
    const userProviders = useLoginUserProviders();
    const linkEmailWithPasswordExecutor = useExecutor('/customers/linkEmailWithPasswordProvider');
    const checkPhoneProvidersExecutor = useExecutor('/customers/checkPhoneProviders');
    const checkEmailProvidersExecutor = useExecutor('/customers/checkEmailProviders');
    const alertPhoneRecognized = useGenericAlert();
    const { update } = useLoginContext();

    const { t } = useTranslation('common');

    const usersLoginInfo = useSelector((state: RootState) => state.usersLoginInfo);

    const registerUser = async (event: FormEvent) => {
        event.preventDefault();
        setLoading(true);
        dispatch(resetAllValidationInfo());

        const res = await checkPhoneProvidersExecutor.post({
            phone: `+${usersLoginInfo.data.phone.fullNumber}`,
        });

        const _providers = res.success ? res.data : [];

        if (_providers.length > 0 && _providers.includes('phone')) {
            alertPhoneRecognized(
                {
                    contentText:
                        'There is already an account with this phone number. Do you want to sign in or enter a new phone number?',
                    cancelText: 'Enter new phone number',
                    successText: 'Sign in',
                },
                () => {
                    setLoading(true);
                    handleSignIn();
                },
                () => {
                    setLoading(false);
                    dispatch(
                        updateUsersLoginInfo({
                            phone: {
                                countryCode: usersLoginInfo.data.phone.countryCode,
                                phoneNumber: '',
                                fullNumber: `${usersLoginInfo.data.phone.countryCode}`,
                            },
                        })
                    );
                }
            );
        } else {
            const res = await checkEmailProvidersExecutor.post({
                email: usersLoginInfo.data.email,
            });
            const _providers = res.success ? res.data : [];

            if (_providers.length > 0 && _providers.includes('phone')) {
                update('userProviders', _providers);
                signInProcess.nextStep('RECOGNIZED');

                return;
            }

            if (!userProviders.includes('password') && userProviders.includes('phone')) {
                // if (signInProcess.hasBeenThrough('LOGIN_WITH_EMAIL')) {
                //     linkEmailWithPasswordExecutor
                //         .post({
                //             email: usersLoginInfo.data.email,
                //             password: usersLoginInfo.data.password,
                //         })
                //         .then(res => {
                //             if (res.success) {
                //                 signInWithEmailAndPassword(usersLoginInfo.data.email, usersLoginInfo.data.password).then(data => {
                //                     postUserToken(data.user);
                //                     dispatch(resetUsersLoginInfo());
                //                     setLoading(false);
                //                     onLogin?.(data.user);
                //                     authentication.closeLogin();
                //                 });
                //             }
                //         });
                // }
            } else {
                const methods = await getSignInMethodsForEmail(usersLoginInfo.data.email);

                const validEmail = methods.filter(m => m === 'password').length === 0;
                if (!validEmail) {
                    setAlertMessage('There is existing user with the email address provided. Do you want to sign in?');
                    setAlertOpen(true);
                    setLoading(false);
                    return;
                }

                checkPhone(`+${usersLoginInfo.data.phone.fullNumber}`).then(res => {
                    if (res.success) {
                        if (res.data.id && !signInProcess.hasBeenThrough('LOGIN_WITH_EMAIL')) {
                            if (!res.data.email) {
                                setAlertMessage(t('phoneWithoutEmail'));
                                setExistingPhonePopupOpen(true);
                            } else {
                                setAlertOpen(true);
                                return;
                            }
                        } else {
                            // signInProcess.nextStep('REGISTER_INFO');
                            if (signInProcess.hasBeenThroughOnlyPhone()) {
                                setLoading(true);
                                verifyPhoneNumber()
                                    .signInWithPhoneNumber(usersLoginInfo.data?.phone.fullNumber)
                                    .then(() => {
                                        setLoading(false);
                                        signInProcess.nextStep('CONFIRM_CODE');
                                    })
                                    .catch((error: Error) => {
                                        setLoading(false);
                                        dispatch(
                                            setValidationInfo(
                                                { hasError: true, message: error.message, errorType: 'global' },
                                                'PHONE_VERIFICATION'
                                            )
                                        );
                                    });
                            } else {
                                if (signInProcess.hasBeenThrough('SOCIAL_GOOGLE')) {
                                    verifyPhoneNumber()
                                        .linkWithPhoneNumber(usersLoginInfo.data?.phone.fullNumber)
                                        .then(() => {
                                            setLoading(false);
                                            signInProcess.nextStep('CONFIRM_CODE');
                                        })
                                        .catch((error: Error) => {
                                            setLoading(false);
                                            dispatch(
                                                setValidationInfo(
                                                    { hasError: true, message: error.message },
                                                    'PHONE_VERIFICATION'
                                                )
                                            );
                                        });
                                } else {
                                    handleVerify();
                                }
                            }
                        }
                    } else {
                        dispatch(setValidationInfo({ hasError: true, message: res.data.message }, 'PHONE_VERIFICATION'));
                        return;
                    }
                });
            }
        }
    };

    const handleVerify = () => {
        setLoading(true);
        dispatch(resetValidationInfo('PHONE_VERIFICATION'));
        verifyPhoneNumber()
            .signInWithPhoneNumber(usersLoginInfo.data?.phone.fullNumber)
            .then(() => {
                setLoading(false);
                signInProcess.nextStep('CONFIRM_CODE');
            })
            .catch((error: Error) => {
                setLoading(false);
                dispatch(
                    setValidationInfo({ hasError: true, message: error.message, errorType: 'global' }, 'PHONE_VERIFICATION')
                );
            });
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        authentication.closeLogin();
    };

    const handleSignIn = () => {
        // signInProcess.goThrough(['LOGIN_WITH_PHONE'], 'LOGIN_WITH_EMAIL');
        verifyPhoneNumber()
            .signInWithPhoneNumber(usersLoginInfo.data?.phone.fullNumber)
            .then(() => {
                setLoading(false);
                signInProcess.goThrough(['LOGIN_WITH_PHONE'], 'CONFIRM_CODE');
            })
            .catch(e => {});
    };

    const handleSignInAgain = () => {
        signInProcess.goThrough([], 'LOGIN_WITH_EMAIL');
        signInProcess.resetProcess();
        dispatch(resetUsersLoginInfo());
        dispatch(resetAllValidationInfo());
    };

    return (
        <Paper
            sx={{
                backgroundColor: theme => theme.palette.background.paper,
                boxShadow: theme => theme.shadows[5],
                // padding: theme.spacing(4, 12),
                minWidth: '300px',
                minHeight: '50vh',
                // justifyContent: 'center',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    width: { xs: '300px', sm: '500px' },
                    paddingTop: { xs: '20px', sm: '50px' },
                    paddingBottom: { xs: '20px', sm: '50px' },
                    paddingLeft: { xs: '20px', sm: '50px' },
                    paddingRight: { xs: '20px', sm: '50px' },
                }}
            >
                <Grid container alignItems="center" direction="column">
                    <Grid item xs={12}>
                        <Typography>{t('createAccount')}</Typography>
                    </Grid>
                </Grid>
                <RegisterInfo onContinue={registerUser} loading={loading} />
            </Box>
            <AlertDialog
                open={alertOpen}
                handleClose={handleAlertClose}
                title={t('weRecognizeYou')}
                actions={[
                    {
                        title: 'Close',
                        color: 'primary',
                        autofocus: false,
                        handler: handleAlertClose,
                    },
                    { title: t('signIn'), color: 'primary', autofocus: true, handler: handleSignIn },
                ]}
            >
                {alertMessage}
            </AlertDialog>
            <AlertDialog
                open={existingPhonePopupOpen}
                handleClose={() => {
                    setExistingPhonePopupOpen(false);
                    setLoading(false);
                }}
                title={t('weRecognizeYou')}
                actions={[
                    {
                        title: 'Close',
                        color: 'primary',
                        autofocus: false,
                        handler: () => {
                            setExistingPhonePopupOpen(false);
                            setLoading(false);
                        },
                    },
                    {
                        title: t('goToEmailSign'),
                        color: 'primary',
                        autofocus: true,
                        handler: handleSignInAgain,
                    },
                ]}
            >
                {alertMessage}
            </AlertDialog>
        </Paper>
    );
};

export default RegisterInfoPaper;
